import { useAuth } from "@clerk/clerk-react";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  InboxOption,
  InboxSidebarOptionKey,
  OutreachContactsContext,
  Suggestion,
  defaultFetchContactsParams,
} from "contexts/OutreachContacts";
import { debounce } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import { fetcherAuth } from "utils/api";

import styles from "./styles";

export default function SearchBar() {
  const { setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { fetchContactsParams, handleFetchContacts, currentSidebarOption } =
    useContext(OutreachContactsContext);
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState<Suggestion[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);

  const handleOnInputChange = (event: CustomEvent, newInputValue: string) => {
    setSearchLoading(true);
    changeInput(newInputValue);
  };

  const changeInput = useMemo(
    () =>
      debounce(async (newSearchQuery: string) => {
        await searchInbox(newSearchQuery);
      }, 150),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentOrg?.id],
  );

  const searchInbox = async (queryString: string) => {
    if (!currentOrg?.id) {
      setSearchLoading(false);
      return;
    }
    try {
      const encodedQuery = encodeURIComponent(queryString);
      let url = `/api/organization/${currentOrg?.id}/outreach-contacts/autocomplete?query=${encodedQuery}`;
      const { autocompleteSuggestions } = await fetcherAuth(getToken, url);
      setSearchResults(autocompleteSuggestions);
    } catch (error) {
      setAlert(`An error occurred ${error}`, "warning");
    } finally {
      setSearchLoading(false);
    }
  };

  const onSelectionChange = (_: CustomEvent, newValue: Suggestion | null) => {
    const updatedParams = { ...fetchContactsParams };
    updatedParams.page = 1;
    updatedParams.selectedSuggestion = newValue;

    // When searching from sent or scheduled tabs, switch to inbox
    if (
      newValue &&
      (currentSidebarOption?.key === InboxSidebarOptionKey.sent ||
        currentSidebarOption?.key === InboxSidebarOptionKey.scheduled)
    ) {
      updatedParams.statuses = InboxOption.statuses;
      updatedParams.scheduledType = null;
      updatedParams.order = defaultFetchContactsParams.order;
      updatedParams.orderBy = defaultFetchContactsParams.orderBy;
      navigate(`/${routes.inboxMain}`);
    }

    handleFetchContacts(updatedParams);
  };

  useEffect(() => {
    searchInbox("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <Autocomplete
      id="inbox-suggestions"
      options={searchResults}
      clearOnBlur
      autoHighlight
      sx={{ width: "100%" }}
      freeSolo={false}
      value={fetchContactsParams.selectedSuggestion}
      onChange={onSelectionChange}
      loading={searchLoading}
      isOptionEqualToValue={(option: Suggestion, value: Suggestion) =>
        option.id === value.id && option.type === value.type
      }
      getOptionLabel={(option: Suggestion) => option.suggestion}
      renderOption={(props, option) => <li {...props}>{option.suggestion}</li>}
      onInputChange={handleOnInputChange}
      // Override Autocomplete Default Filter Function with BE searching.
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          sx={styles.root}
          {...params}
          placeholder="Search Inbox"
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <Box
                component="i"
                className="fa-regular fa-search"
                sx={{ px: 2 }}
              />
            ),
            endAdornment: (
              <>
                {searchLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
